import { useQueryClient } from '@tanstack/react-query';

import {
  getHostingAdvancedServerQueryKey,
  useHostingAdvancedServerStatus,
} from '@newfold/huapi-js';

import { NullableString } from '~/utils/hosting/types';

type ServerAction = 'start' | 'stop' | 'reboot';

interface ToggleParams {
  hostingId: NullableString | undefined;
}

const getPayloadForAction = (action: ServerAction) => {
  switch (action) {
    case 'start':
      return { status: 'start', action: 'start' } as const;
    case 'stop':
      return { status: 'stop', action: 'stop' } as const;
    case 'reboot':
      return { status: 'reboot_soft', action: 'reboot' } as const;
    default:
      return { status: 'start', action: 'start' } as const;
  }
};

/**
 * useServerStatusToggle
 *
 * Encapsulates the server status mutation (start/stop/reboot) with
 * optimistic updates and cache invalidation.
 *
 * Usage:
 * const { start, stop, reboot, toggle, handleServerStatus, handleRebootServer } = useServerStatusToggle({ hostingId });
 */
export const useServerStatusToggle = ({ hostingId }: ToggleParams) => {
  const queryClient = useQueryClient();
  const advancedServerQueryKey = getHostingAdvancedServerQueryKey(
    hostingId as any,
  );

  const { mutate } = useHostingAdvancedServerStatus({
    mutation: {
      onMutate: (newStatus: any) => {
        // @ts-ignore - query data shape comes from huapi-js
        const oldStatus = queryClient.getQueryData(advancedServerQueryKey)?.data
          .status;
        queryClient.setQueryData(advancedServerQueryKey, newStatus);
        return { oldStatus, newStatus };
      },
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: advancedServerQueryKey });
      },
    },
  });

  const toggle = (action: ServerAction) => {
    const payload = getPayloadForAction(action);
    mutate({ hostingId: hostingId as any, data: payload });
    return action;
  };

  const start = () => toggle('start');
  const stop = () => toggle('stop');
  const reboot = () => toggle('reboot');

  // Convenience handler: if current status is 'active', issue 'stop'; otherwise 'start'.
  const handleServerStatus = (currentStatus: string) => {
    const action: ServerAction = currentStatus === 'active' ? 'stop' : 'start';
    return toggle(action);
  };

  // Convenience handler for reboot: if 'active' then reboot, else start.
  const handleRebootServer = (currentStatus: string) => {
    const action: ServerAction =
      currentStatus === 'active' ? 'reboot' : 'start';
    return toggle(action);
  };

  return {
    toggle,
    start,
    stop,
    reboot,
    handleServerStatus,
    handleRebootServer,
  };
};

export default useServerStatusToggle;
